import { motion } from "motion/react";
import { bool, node, number, object, string } from "prop-types";
import { useMemo, useRef } from "react";

import styled from "styled-components";

import FnrTypography from "UI/FnrTypography";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useHeight from "Utilities/Hooks/useHeight";
import useWidth from "Utilities/Hooks/useWidth";

const DefaultLabel = ({
  hasValue,
  label,
  left: leftArg,
  children,
  inputRef,
  topAlign,
  icon,
  ...rest
}) => {
  const labelRef = useRef();
  const inputHeight = useHeight(inputRef);
  const labelWidth = useWidth(labelRef);
  // no prefix: sit at the input's own padding, which is the wrapper's token
  const left = leftArg ? `${leftArg}px` : "var(--fnr-input-padding-x)";

  const labelAnimate = useMemo(() => {
    if (hasValue)
      return {
        top: "0px",
        transform: "translateY(-60%)",
      };
    if (topAlign) {
      return { top: "var(--fnr-input-label-top)" };
    }
    if (inputHeight)
      return { top: `${inputHeight / 2}px`, transform: "translateY(-50%)" };
    return { top: `50%`, transform: "translateY(-50%)" };
  }, [hasValue, inputHeight, topAlign]);

  return (
    <>
      <LineHider
        data-cy="DefaultLabel_LineHider"
        $width={`${labelWidth ? labelWidth + 4 : 0}px`}
        $left={`calc(${left} - 2px)`}
        $show={hasValue}
      />
      <LabelPos
        initial={false}
        data-cy="DefaultLabel_LabelPos"
        animate={labelAnimate}
        transition={{ duration: 0.2, type: "spring" }}
        $left={left}
      >
        <LabelMeasure ref={labelRef}>
          <FnrTypography
            fontSize={hasValue ? "sm" : "lg"}
            fontWeight={300}
            color={
              hasValue ? addOpacity("#333333", 0.8) : addOpacity("#333333", 0.4)
            }
            {...rest}
          >
            {label}
          </FnrTypography>
        </LabelMeasure>
      </LabelPos>
    </>
  );
};

const LabelPos = styled(motion.div)`
  position: absolute;
  left: ${({ $left }) => $left};
  transition: left 300ms;
  pointer-events: none;
  white-space: nowrap;
`;

// `useWidth` reads clientWidth, which is 0 for inline elements (the
// FnrTypography span). Wrap in an inline-block so the LineHider gets a
// real width to cover the input's border with.
const LabelMeasure = styled.span`
  display: inline-block;
`;

const opacity = ({ $show }) => {
  if ($show)
    return `
    opacity: 1;
    transition: opacity 300ms;
  `;
  return `
    opacity: 0;
    transition: opacity 50ms;
  `;
};

const LineHider = styled.div`
  position: absolute;
  border-bottom: 4px solid;
  left: ${({ $left }) => $left};
  width: ${({ $width }) => $width};
  border-color: var(--fnr-input-bg);
  top: -2px;
  z-index: 0;
  pointer-events: none;
  ${opacity}
`;

DefaultLabel.propTypes = {
  hasValue: bool,
  label: string,
  inputRef: object,
  children: node,
  left: number,
  topAlign: bool,
};

export default DefaultLabel;
